import { useCallback, useRef, useState } from "react";

import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTypeahead,
} from "@floating-ui/react";

import type { Placement, VirtualElement } from "@floating-ui/react";

export function useContextMenu({
  onOpenChange,
  placement = "bottom-start",
}: {
  onOpenChange?: (open: boolean) => void;
  placement?: Placement;
} = {}) {
  const [open, setOpenState] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const setOpen = useCallback(
    (next: boolean) => {
      setOpenState(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  const handleTypeaheadMatch = useCallback(
    (index: number | null) => {
      if (open) {
        setActiveIndex(index);
      }
    },
    [open],
  );

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ availableHeight, elements }) {
          elements.floating.style.maxHeight = `${Math.max(0, availableHeight)}px`;
        },
      }),
    ],
  });

  const listRef = useRef<Array<HTMLElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "menu" });
  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    loop: true,
    virtual: true,
    openOnArrowKeyDown: false,
    focusItemOnHover: false,
  });
  const typeahead = useTypeahead(context, {
    listRef: labelsRef,
    activeIndex,
    onMatch: handleTypeaheadMatch,
  });
  const interactions = useInteractions([dismiss, role, listNavigation, typeahead]);

  const openAtReference = useCallback(
    (reference: Element | VirtualElement) => {
      refs.setPositionReference(reference);
      setOpen(true);
    },
    [refs, setOpen],
  );

  const openAtPoint = useCallback(
    (x: number, y: number) => {
      openAtReference({
        getBoundingClientRect: () => ({
          x,
          y,
          top: y,
          left: x,
          right: x,
          bottom: y,
          width: 0,
          height: 0,
          toJSON: () => ({}),
        }),
      });
    },
    [openAtReference],
  );

  const close = useCallback(() => setOpen(false), [setOpen]);

  return {
    open,
    setOpen,
    close,
    openAtPoint,
    openAtReference,
    activeIndex,
    refs,
    floatingStyles,
    context,
    listRef,
    labelsRef,
    ...interactions,
  };
}

export type ContextMenuApi = ReturnType<typeof useContextMenu>;
